import {BaseSensorConfigView, BaseSensorConfigViewOptions} from "./_BaseSensorConfigView";
import * as $ from "jquery";
import * as _ from "lodash";
import {ModbusOperationView} from "./ModbusOperation";
import {TemplateConversionFunctionCollection} from "../../../collections/TemplateConversionFunctions";
import {TemplateChannelParamCollection} from "../../../collections/TemplateChannelParams";
import {ChannelTag} from "../../../collections/ChannelTags";
import {ModbusWriteView} from "./ModbusWriteView";
import {ActionPacketCollection} from "../../../collections/ActionPackets";
import {CopyModbusConfigModal} from "../../modals/CopyModbusConfigModal";

interface SensorLimits {
    maxRegisters: number;
    maxRangeLength: number;
}

export class ModbusMasterConfigView extends BaseSensorConfigView {

    private modbusOperationViews: ModbusOperationView[];
    private maxOpNum: number;
    private initialConfig: string;
    private connector: number;
    private collectionLoaded: boolean;
    private templateConversionFunctions: TemplateConversionFunctionCollection;
    private templateChannelParams: TemplateChannelParamCollection;
    private sensorLimits: SensorLimits;
    currentConfigAndChannelInfo: any;
    private currentRegisters: any;
    private updatedChannelsAndParams: any;
    private actionPackets: ActionPacketCollection;
    private modbusWriteView: ModbusWriteView;
    private template = _.template(require('templates/settings/sensor_config/modbusmaster.html'));
    private sensorConfigRecordsPerPacket = 69;   // A single update packet has this many records in this.config
    private modbusRegistersPerUpdatePacket = 20; // How many single registers can be stored in a single update packet
    private copyConfigModal: any = null;

    constructor(options: BaseSensorConfigViewOptions) {
        super(_.extend({
            events: {
                "click .settings-sensorconfig-modbusmaster-add-operation": 'handleClickAddOperation',
                "click .settings-sensorconfig-modbusmaster-copy-config": 'handleClickCopyConfig',
            }
        }, options));

        this.modbusOperationViews = [];
        this.maxOpNum = 1;
        this.initialConfig = null;
        this.connector = this.con['sensor_connector'];

        this.collectionLoaded = false;
        this.templateConversionFunctions = new TemplateConversionFunctionCollection([], {
            sensorType: this.con['sensor_type'],
            sensorVersion: this.con['sensor_version']
        });
        this.templateChannelParams = new TemplateChannelParamCollection([], {
            sensorType: this.con['sensor_type'],
            sensorVersion: this.con['sensor_version']
        });

        this.actionPackets = new ActionPacketCollection([], {installId: this.installId});
        this.modbusWriteView = null;

        // Setup the buffer limits of the sensor bridge.
        // This limits changed in version 3.4 since it uses a bigger microprocessor with more storage
        if (this.con['sensor_firmware_version'] >= 340) {
            this.sensorLimits = {
                maxRegisters: 1000,
                maxRangeLength: 150
            };
        } else {
            this.sensorLimits = {
                maxRegisters: 60,
                maxRangeLength: 50
            };
        }

        this.createInitialReadOperationViews();

        // These fields will be populated when saving
        this.currentConfigAndChannelInfo = null;
        this.updatedChannelsAndParams = null;
    }

    hasUpdatedConfig() {
        return JSON.stringify(this.currentConfigAndChannelInfo) !== this.initialConfig;
    }

    prepareForSaving() {
        this.currentConfigAndChannelInfo = this.getConfigAndChannelInfo();
        this.updatedChannelsAndParams = this.getUpdatedChannelsAndParams();
        this.currentRegisters = this.currentConfigAndChannelInfo[2];
    }

    validate() {
        var errors = [];

        // Check that we're not sending more registers than the sensor supports
        // This simultaneously also checks that we're not sending more update packets, since the limits match
        var maxNumUpdatePkts = this.sensorLimits['maxRegisters'] / this.modbusRegistersPerUpdatePacket;
        if (this.currentConfigAndChannelInfo[0].length > maxNumUpdatePkts * this.sensorConfigRecordsPerPacket) {
            errors.push('Too many operations! You may only configure up to ' + this.sensorLimits['maxRegisters'] +
                ' individual register addresses or ' + (this.sensorLimits['maxRegisters'] / 2) + ' address ranges.');
        }

        var dups = this.hasDuplicates();
        if (dups) {
            errors.push('Duplicate Address: ' + dups[0] + ' - Function: ' + dups[1] + ' - Register: ' + dups[2]);
        }

        var rangeErrors = this.validateRanges();
        for (var i = 0; i < rangeErrors.length; i++) errors.push(rangeErrors[i]);

        var errorContainer = this.$('.modbus-alerts');
        errorContainer.empty();
        if (errors.length) {
            var td = $('<td>').attr('colspan', 2).html(errors.join('<br>'));
            errorContainer.removeClass('hidden').append(td);
        } else {
            errorContainer.addClass('hidden');
        }

        var valid = true;
        _.each(this.modbusOperationViews, function (view) {
            if (view.removed) return;

            var subViewErrors = view.validate();
            // Always render to clear old errors
            //view.render(subViewErrors);
            if (valid) valid = !subViewErrors.length;
        });

        return !(errors.length) && valid;
    };

    private validateRanges() {
        var errors = [];

        // Check that each range has only up to this.sensorLimits['maxRangeLength'] registers
        var config = this.currentConfigAndChannelInfo[0];
        for (var i = 0; i < config.length; i++) {
            if (config[i].type.indexOf('ADDR') === -1) continue;

            var addr = config[i].value;
            var func = config[i + this.modbusRegistersPerUpdatePacket].value;
            var reg = config[i + (this.modbusRegistersPerUpdatePacket * 2)].value;

            if (addr === 0 && func === 0 && reg === 0) continue;

            var range = reg & (1 << 15);
            if (range) {
                reg = reg % 16384;
                var reg2 = config[i + 41].value % 16384;

                if (reg2 - reg > this.sensorLimits['maxRangeLength']) {
                    errors.push(
                        'This maximum length of a register range is ' + this.sensorLimits['maxRangeLength'] +
                        ' registers. The range (' + reg + ' - ' + reg2 + ') for deivce address ' + addr + ' is ' + (reg2 - reg) +
                        ' registers long.'
                    )
                }
                i++;
            }
        }

        return errors;
    };

    private hasDuplicates() {
        var config = this.currentConfigAndChannelInfo[0];
        var seen = [];

        for (var i = 0; i < config.length; i++) {
            if (config[i].type.indexOf('ADDR') === -1) continue;

            var addr = config[i].value;
            var func = config[i + this.modbusRegistersPerUpdatePacket].value;
            var reg = config[i + (this.modbusRegistersPerUpdatePacket * 2)].value;

            if (addr === 0 && func === 0 && reg === 0) continue;

            var range = reg & (1 << 15);
            var _32bit = (reg & (1 << 14)) > 0;
            if (range) {
                var incr = _32bit ? 2 : 1;
                var reg2 = config[i + 41].value;

                for (var j = reg; j <= reg2; j += incr) {
                    var str = addr + '-' + func + '-' + (j % 16384) + (_32bit ? '32bit' : '16bit');
                    if (_.includes(seen, str)) return [addr, func, (j % 16384)];
                    seen.push(str);
                }
                i++;
            } else {
                var str = addr + '-' + func + '-' + (reg % 16384) + (_32bit ? '32bit' : '16bit');
                if (_.includes(seen, str)) return [addr, func, (reg % 16384)];
                seen.push(str);
            }
        }

        return false;
    };

    private createNewModbusOperationView(initial, operations) {
        return new ModbusOperationView({
            permissionCollection: this.permissionCollection,
            conversionFunctionCollection: this.conversionFunctionCollection,
            channelParamCollection: this.channelParamCollection,
            templateConversionFunctions: this.templateConversionFunctions,
            templateChannelParams: this.templateChannelParams,
            installId: this.installId,
            opNumber: this.maxOpNum,
            connector: this.connector,
            con: this.con,
            operations: operations,
            initial: initial
        });
    };

    private createNewModbusWriteView() {
        return new ModbusWriteView({
            currentInstallId: this.installId,
            permissionCollection: this.permissionCollection,
            actionPackets: this.actionPackets,
            con: this.con,
            module: this.module,
            sensorId: this.sensorId,
            config: this.config
        });
    };

    public getOperations() {
        var sectionsPerPacket = this.sensorConfigRecordsPerPacket; // 69 configuration sections per modbus configuration packet
        var numPackets = this.config.length / sectionsPerPacket;
        var operations = {};
        var currentPosition = 1;
        for (var i = 0; i < numPackets; i++) {
            // Loop through all 20 registers and create operations, skipping registers that are 0
            // and packing two together if it's a range
            for (var j = 0; j < this.modbusRegistersPerUpdatePacket; j++) {
                var _32bitReg = false;
                var regRange = false;

                var devAddr = this.config.find((c) => {
                    return c.get('order') === ((8 + j) + (i * sectionsPerPacket))
                }).get('value');

                var modbusFunc = this.config.find((c) => {
                    return c.get('order') === ((28 + j) + (i * sectionsPerPacket))
                }).get('value');

                var reg = this.config.find((c) => {
                    return c.get('order') === ((48 + j) + (i * sectionsPerPacket))
                }).get('value');

                var reg2 = null;

                // if the device, func, and register are 0, its wrong
                if ((devAddr === 0 && modbusFunc === 0 && reg === 0) || devAddr === undefined || modbusFunc === undefined || reg === undefined) {
                    continue;
                }


                // bitwise & with 16384 will be true if its a 32 bit register
                if (reg & (1 << 14)) {
                    _32bitReg = true;
                }


                // bitwise & with 32768 means beginning of range
                if (reg & (1 << 15)) {
                    // This is the start of a range
                    reg2 = this.config.find(function (c) {
                        return c.get('order') === ((48 + j + 1) + (i * sectionsPerPacket))
                    });

                    // no register 2 here means singe register range
                    if (!reg2) continue;

                    reg2 = reg2.get('value');
                    regRange = true;
                    // Skip the next register
                    j++;
                }


                // initialize device object
                if (!_.has(operations, devAddr)) {
                    operations[devAddr] = {};
                }

                if (!_.has(operations[devAddr], modbusFunc)) {
                    operations[devAddr][modbusFunc] = [];
                }

                // mod by 2^14 to get the register number
                reg = reg % 16384;
                reg2 = reg2 % 16384;

                // Since both ends are inclusive we must add 1 for 16bit registers and 2 for 32 bit
                var numPos = regRange ? ((reg2 - reg) + (_32bitReg ? 2 : 1)) : 1;

                if (regRange && _32bitReg) numPos = numPos / 2;
                if (regRange) {

                    operations[devAddr][modbusFunc].push({
                        type: 'range',
                        bits: _32bitReg ? 32 : 16,
                        register: [reg, reg2],
                        position: currentPosition,
                        numPositions: numPos
                    });
                } else {
                        operations[devAddr][modbusFunc].push({
                        type: 'single',
                        bits: _32bitReg ? 32 : 16,
                        register: [reg],
                        position: currentPosition,
                        numPositions: numPos
                    });
                }
                currentPosition += numPos;
            }
        }

        return operations;
    }

    private createInitialReadOperationViews() {
        var operations = this.getOperations();
        _.each(operations, (functions, device) => {
            _.each(functions, (ops, func) => {
                this.modbusOperationViews.push(this.createNewModbusOperationView(true, {
                    registers: ops,
                    device: Number(device),
                    func: Number(func)
                }));
                this.maxOpNum++;
            });
        });
    };

    private handleClickAddOperation() {
        var view = this.createNewModbusOperationView(false, {
            registers: [{
                type: 'single',
                bits: 16,
                register: [0],
                position: null,
                numPositions: 1
            }],
            device: null,
            func: 3
        });
        this.maxOpNum++;
        this.modbusOperationViews.push(view);
        this.renderModbusOperationView(view);
    }

    private handleClickCopyConfig() {
        const options = this.options as any;
        if (!options.organizationCollection || !options.installCollection) {
            return;
        }
        
        // Check if modal element exists in DOM, if not, recreate it
        const modalExists = $('#copyModbusConfigModal').length > 0;
        
        if (!this.copyConfigModal || !modalExists) {
            // Remove old modal if it exists but element is missing
            if (this.copyConfigModal && !modalExists) {
                try {
                    this.copyConfigModal.remove();
                } catch (e) {}
            }
            
            this.copyConfigModal = new CopyModbusConfigModal({
                modelId: 0, // Required by BaseModifyModalViewOptions but not used
                organizationCollection: options.organizationCollection,
                installCollection: options.installCollection,
                permissionCollection: this.permissionCollection,
                targetSensorId: this.sensorId,
                targetSensorName: this.con['sensor_name'] || `Sensor ${this.connector}`,
                onSuccess: () => {
                    // Reload the page to show updated configuration
                    window.location.reload();
                }
            } as any);
            this.copyConfigModal.render();
            $('body').append(this.copyConfigModal.$el);
        }
        
        this.copyConfigModal.show();
    }

    private renderModbusOperationView(view: ModbusOperationView) {
        var render = view.render();
        this.$('.settings-sensorconfig-modbusmaster-operation-container').append(render.$el);
        this.delegateEvents();
    };

    private renderModbusWriteView(view: ModbusWriteView) {
        var render = view.render();
        this.$('.settings-sensorconfig-modbusmaster-operation-container-write').append(render.$el);
        this.delegateEvents();
    };

    render(errors?) {
        this.$el.html(this.template({
            config: this.config,
            errors: errors,
            module: this.module,
            con: this.con,
            permissionCollection: this.permissionCollection,
            installId: this.installId,
            collectionLoaded: this.collectionLoaded
        }));

        if (!this.collectionLoaded) {
            $.when(
                this.templateChannelParams.fetch({reset: true}),
                this.templateConversionFunctions.fetch({reset: true}),
                this.actionPackets.fetch({reset: true})
            ).done(() => {
                this.collectionLoaded = true;
                this.render();
            });
        } else {
            _.each(this.modbusOperationViews, (view) => {
                if (!view.$el.is(':visible')) this.renderModbusOperationView(view);
            });

            //only need one modbus write view
            this.modbusWriteView = this.createNewModbusWriteView();
            this.renderModbusWriteView(this.modbusWriteView);

            if (!this.initialConfig) {
                this.initialConfig = JSON.stringify(this.getConfigAndChannelInfo());
            }
        }
        return this
    };

    getConfig() {
        return this.currentConfigAndChannelInfo[0];
    };

    getActionPackets() {
        return this.modbusWriteView.getActionPackets();
    }

    private getConfigAndChannelInfo() {
        var sensorId = this.sensorId;
        var channels = [];
        // To determine the number of packets, we must first build all operations
        var opsAddr = [];
        var opsFuncs = [];
        var opsRegs = [];
        var c = 1;
        var ops = [];

        _.each(this.modbusOperationViews, (view) => {
            if (view.removed) return;
            ops = ops.concat(view.getConfigAndChannelInfo());
        });

        // Sort ops so that ranges are first, ordered by devices
        ops.sort(function (a, b) {
            if ((a.isRange && b.isRange) || (!a.isRange && !b.isRange)) {
                var d = b.device - a.device;
                return d === 0 ? (a.registers[0] - b.registers[0]) : d;
            }
            return a.isRange ? -1 : 1;
        });
        // registers is for controls TODO, avoid this hack
        var registers = [];
        _.each(ops, (operation) => {
            opsAddr.push({
                sensor_id: sensorId,
                type: "ADDR" + c,
                value: operation.device
            });
            opsFuncs.push({
                sensor_id: sensorId,
                type: "FUNC" + c,
                value: operation.func
            });
            opsRegs.push({
                sensor_id: sensorId,
                type: "REG" + c,
                value: ((Number(operation.isRange) << 15) | (Number(operation.is32bit) << 14) | Number(operation.registers[0]))
            });
            c = (c % this.modbusRegistersPerUpdatePacket) + 1;
            if (operation.isRange) {
                opsAddr.push({
                    sensor_id: sensorId,
                    type: "ADDR" + c,
                    value: operation.device
                });
                opsFuncs.push({
                    sensor_id: sensorId,
                    type: "FUNC" + c,
                    value: operation.func
                });
                opsRegs.push({
                    sensor_id: sensorId,
                    type: "REG" + c,
                    value: ((Number(operation.isRange) << 15) | (Number(operation.is32bit) << 14) | Number(operation.registers[1]))
                });
                c = (c % this.modbusRegistersPerUpdatePacket) + 1;
            }

            channels = channels.concat(operation.channels);
            var deviceRegesters = {};
            deviceRegesters[String(operation.device)] = operation.registers;
            registers.push(deviceRegesters);
        });
        if (opsAddr.length === 0) {
            var nb32 = 0;
        } else if (opsAddr.length % this.modbusRegistersPerUpdatePacket === 0) {
            var nb32 = this.modbusRegistersPerUpdatePacket;
        } else {
            var nb32 = opsAddr.length % this.modbusRegistersPerUpdatePacket;
        }

        // Fill the last packet with zero configs
        while (opsAddr.length % this.modbusRegistersPerUpdatePacket !== 0 || opsAddr.length < this.modbusRegistersPerUpdatePacket) {
            var c = (opsAddr.length % this.modbusRegistersPerUpdatePacket) + 1;
            opsAddr.push({
                sensor_id: sensorId,
                type: "ADDR" + c,
                value: 0
            });
            opsFuncs.push({
                sensor_id: sensorId,
                type: "FUNC" + c,
                value: 0
            });
            opsRegs.push({
                sensor_id: sensorId,
                type: "REG" + c,
                value: 0
            });
        }
        var opsConf = [];

        // for every chunk of 20 slice that interval and concat to the config
        for (var i = 0; i < (opsAddr.length / this.modbusRegistersPerUpdatePacket); i++) {
            opsConf = opsConf.concat(
                opsAddr.slice(i * this.modbusRegistersPerUpdatePacket, (i + 1) * this.modbusRegistersPerUpdatePacket),
                opsFuncs.slice(i * this.modbusRegistersPerUpdatePacket, (i + 1) * this.modbusRegistersPerUpdatePacket),
                opsRegs.slice(i * this.modbusRegistersPerUpdatePacket, (i + 1) * this.modbusRegistersPerUpdatePacket)
            )
        }
        var config = [];
        var parity = this.$('.settings-sensorconfig-modbusmaster-parity').val();
        var delay = this.$('.settings-sensorconfig-modbusmaster-delay').val();
        var endianness = this.$('.settings-sensorconfig-modbusmaster-endianness').val();
        var baudRate = this.$('.settings-sensorconfig-modbusmaster-baud-rate').val();
        var numPackets = Math.max(Math.ceil(opsConf.length / (3 * this.modbusRegistersPerUpdatePacket)), 1);
        for (var i = 0; i < numPackets; i++) {
            var opsSection = opsConf.slice(i * 60, (i + 1) * 60);
            config = config.concat([{
                sensor_id: sensorId,
                type: "INSTRUCTION",
                value: 0
            }, {
                sensor_id: sensorId,
                type: "NB32",
                value: i === (numPackets - 1) ? nb32 : this.modbusRegistersPerUpdatePacket
            }, {
                sensor_id: sensorId,
                type: "DELAY",
                value: Number(delay)
            }, {
                sensor_id: sensorId,
                type: "PARITY",
                value: Number(parity)
            }, {
                sensor_id: sensorId,
                type: "UNIFORM",
                value: 0
            }, {
                sensor_id: sensorId,
                type: "ENDIAN_SWAP",
                value: Number(endianness)
            }, {
                sensor_id: sensorId,
                type: "BAUD_RATE",
                value: Number(baudRate)
            }]).concat(opsSection);
            config.push({
                sensor_id: sensorId,
                type: "SPACE2",
                value: 0
            }, {
                sensor_id: sensorId,
                type: "SPACE3",
                value: 0
            })
        }
        return [config, channels, registers]
    };

    getChannelsForConfigAndInfo(configAndChannelInfo) {
        var config = configAndChannelInfo[0];
        var channelInfo = configAndChannelInfo[1];
        var channels = [];
        var channelCounter = 0;
        for (var i = 0; i < config.length; i++) {
            if (config[i].type.indexOf('ADDR') === -1) continue;
            var addr = config[i].value;
            var func = config[i + this.modbusRegistersPerUpdatePacket].value;
            var reg = config[i + (2 * this.modbusRegistersPerUpdatePacket)].value;
            if (addr === 0 && func === 0 && reg === 0) continue;
            var range = reg & (1 << 15);
            var _32bit = (reg & (1 << 14)) > 0;
            if (range) {
                var incr = _32bit ? 2 : 1;
                var reg2 = config[i + 1 + (2 * this.modbusRegistersPerUpdatePacket)].value;

                for (var j = reg; j <= reg2; j += incr) {
                    channels.push(_.extend({
                        addr: addr,
                        func: func,
                        reg: j % 16384,
                        '32bit': _32bit
                    }, channelInfo[channelCounter]));
                    channelCounter++;
                }
                i++;
            } else {
                channels.push(_.extend(
                    {addr: addr, func: func, reg: reg % 16384, '32bit': _32bit},
                    channelInfo[channelCounter]
                ));
                channelCounter++;
            }
        }
        return channels;
    };

    private getUpdatedChannelsAndParams() {
        var channelsToCreate = [];
        var channelsToUpdate = {};
        var paramsToUpdate = {};
        // Always delete position 0 for modbus, since this is just a template channel
        var positionsToDelete = [0];

        var initialChannels = this.getChannelsForConfigAndInfo(JSON.parse(this.initialConfig));
        var channels = this.getChannelsForConfigAndInfo(this.currentConfigAndChannelInfo);

        // Get all new and update blocks
        for (var i = 0; i < channels.length; i++) {
            var matchFound = false;
            var addr = channels[i].addr;
            var func = channels[i].func;
            var reg = channels[i].reg;

            for (var j = 0; j < initialChannels.length; j++) {
                if (addr === initialChannels[j].addr
                    && func === initialChannels[j].func
                    && reg === initialChannels[j].reg) {

                    var oldPosition = j + 1;
                    var existingChannel = _.find(this.con['channels'], {channel_position: oldPosition});
                    var id = existingChannel['channel_id'];
                    var conversionFunction = this.conversionFunctionCollection.findWhere({
                        channel_id: id,
                        name: channels[i].conversion_function_name
                    });

                    // Create Channel Update
                    if (j !== i) {
                        channelsToUpdate[id] = {
                            id: id,
                            oldPosition: j + 1,
                            position: i + 1,
                            name: channels[i].name,
                            unit: channels[i].unit,
                            decimals: channels[i].decimals,
                            conversion_function_id: conversionFunction ? conversionFunction.get('id') : null
                        };
                    } else {
                        channelsToUpdate[id] = {
                            id: id,
                            position: i + 1,
                            name: channels[i].name,
                            unit: channels[i].unit,
                            decimals: channels[i].decimals,
                            conversion_function_id: conversionFunction ? conversionFunction.get('id') : null
                        };
                    }

                    // Create Param Updates
                    _.each(channels[i].params, (p: ChannelTag) => {
                        var param = this.channelParamCollection.findWhere({
                            channel_id: id,
                            key: p.key
                        });
                        var paramId = param.get('id');
                        paramsToUpdate[paramId] = {
                            id: paramId,
                            value: p.value
                        }
                    });

                    matchFound = true;
                    break;
                }
            }

            if (!matchFound) {
                channelsToCreate.push({
                    sensor_id: this.sensorId,
                    position: i + 1,
                    name: channels[i].name,
                    unit: channels[i].unit,
                    decimals: channels[i].decimals,
                    conversion_function_name: channels[i].conversion_function_name,
                    params: channels[i].params
                })
            }
        }

        // Get all deleted blocks
        for (i = 0; i < initialChannels.length; i++) {
            matchFound = false;
            for (j = 0; j < channels.length; j++) {
                if (channels[j].addr === initialChannels[i].addr
                    && channels[j].func === initialChannels[i].func
                    && channels[j].reg === initialChannels[i].reg) {

                    matchFound = true;
                    break;
                }
            }

            if (!matchFound) {
                positionsToDelete.push(i + 1);
            }
        }
        var idsToDelete = [];
        _.each(positionsToDelete, (pos) => {
            var existingChannel = _.find(this.con['channels'], {channel_position: pos});
            if (existingChannel) idsToDelete.push(existingChannel['channel_id']);
        });
        return [
            {create: channelsToCreate, update: channelsToUpdate, 'delete': idsToDelete},
            {create: [], update: paramsToUpdate, 'delete': []}
        ]
    };

    getUpdatedChannels() {
        return this.updatedChannelsAndParams[0];
    };

    getUpdatedChannelParams() {
        return this.updatedChannelsAndParams[1];
    };

    getUpdatedRegisters() {
        return this.currentRegisters;
    };

}


// WEBPACK FOOTER //
// ./srv/public/js/views/settings/sensor_config/ModbusMasterConfigView.ts